import React, { useEffect, useState, useRef } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { BarChart3, Save, FileDown, Trash2, ImagePlus, Eye, Send, CheckCircle2 } from "lucide-react";
import { exportElementAsPDF } from "@/lib/pdf";
import { academicYearOf } from "@/lib/dates";
import SchoolDocumentHeader from "@/components/SchoolDocumentHeader";
import { useAuth } from "@/contexts/AuthContext";
import RecordApproval from "@/components/RecordApproval";
import PrintSignatures from "@/components/PrintSignatures";
import { staffTitles } from "@/lib/staffTitles";

const MONTHS = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];

const PROGRAMS = [
  "التهيئة النفسية", "التوعية بالألعاب الإلكترونية", "خفض العنف (رفق)",
  "تعزيز السلوك الإيجابي", "تنمية الدافعية", "التوعية بالتدخين والمخدرات",
  "التوجيه المهني", "التوجيه وقت الأزمات", "تكريم المتفوقين",
  "خصائص النمو", "متابعة الضعفاء", "الانضباط المدرسي",
];

const CASE_TYPES = [
  "غياب متكرر", "تأخر متكرر", "تنمر", "إهمال", "المواقف اليومية",
  "جلسات فردي", "زيارات الفصول", "تواصل أولياء", "تحويل من إدارة",
  "حالات تحسنت", "حالات مستمرة", "تحتاج تدخل مشرف",
];

const emptyReport = () => ({
  month: MONTHS[new Date().getMonth()],
  programs: PROGRAMS.reduce((a, p) => ({ ...a, [p]: { done: false, notes: "" } }), {}),
  cases: CASE_TYPES.reduce((a, c) => ({ ...a, [c]: 0 }), {}),
  photos: [], // array of base64 strings (max 3)
  suggestions: "",
  counselor_name: "",
  principal_name: "",
});

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function MonthlyReport({ readOnly = false, counselorId = null, counselorName = "", counselorGender = "", schoolKey = null, schoolName = "" }) {
  const { user } = useAuth();
  readOnly = readOnly || user?.role !== "counselor" || Boolean(counselorId && counselorId !== user?.id);
  const ownerGender = counselorGender || (!counselorId && user?.role === "counselor" ? user.gender : "");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // record id or null (new)
  const [form, setForm] = useState(emptyReport());
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [year, setYear] = useState("all");
  const [principal, setPrincipal] = useState({ name: "", gender: "" });
  const printRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const params = { type: "monthly_report" };
      if (counselorId) params.counselor_id = counselorId;
      const { data } = await api.get("/records", { params });
      setReports(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [counselorId]);

  useEffect(() => {
    let active = true;
    const accountCounselorName = counselorName || (!counselorId && user?.role === "counselor" ? user.name : "");
    setForm((current) => ({ ...current, counselor_name: accountCounselorName }));
    api.get("/school-principal", { params: counselorId ? { counselor_id: counselorId } : {} })
      .then(({ data }) => {
        if (!active) return;
        setPrincipal(data);
        setForm((current) => ({
          ...current,
          counselor_name: accountCounselorName,
          principal_name: data.name || "",
        }));
      })
      .catch((error) => toast.error(formatErr(error)));
    return () => { active = false; };
  }, [user?.name, user?.role, counselorId, counselorName]);

  const save = async () => {
    try {
      const reportData = {
        ...form,
        counselor_name: form.counselor_name || counselorName || (!counselorId && user?.role === "counselor" ? user.name : ""),
        principal_name: form.principal_name || principal.name || "",
      };
      if (editing) {
        await api.put(`/records/${editing}`, { data: reportData });
        toast.success("تم تحديث التقرير");
      } else {
        await api.post("/records", { type: "monthly_report", data: reportData });
        toast.success("تم حفظ التقرير الشهري");
      }
      setForm(emptyReport()); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const editReport = (r) => {
    setEditing(r.id);
    setForm({
      ...emptyReport(),
      ...r.data,
      counselor_name: r.data.counselor_name || counselorName || (!counselorId && user?.role === "counselor" ? user.name : ""),
      principal_name: r.data.principal_name || principal.name || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("حذف التقرير؟")) return;
    try { await api.delete(`/records/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const addPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const existingBytes = form.photos.reduce((total, photo) => total + Math.floor((photo.length * 3) / 4), 0);
    const addedBytes = files.reduce((total, file) => total + file.size, 0);
    if (existingBytes + addedBytes > 6 * 1024 * 1024) {
      toast.error("تجاوزت الصور حد الحجم الإجمالي المسموح به (6 ميجابايت)");
      e.target.value = "";
      return;
    }
    try {
      const b64 = await Promise.all(files.map(fileToBase64));
      setForm({ ...form, photos: [...form.photos, ...b64] });
      e.target.value = "";
    } catch {
      toast.error("تعذر قراءة الصورة");
      e.target.value = "";
    }
  };
  const removePhoto = (i) => setForm({ ...form, photos: form.photos.filter((_, idx) => idx !== i) });

  const exportPDF = () => exportElementAsPDF(printRef.current, `تقرير-شهري-${form.month}.pdf`, { schoolKey, schoolName });

  const submitToSupervisor = async (id) => {
    if (!window.confirm("إرسال هذا التقرير للمشرف؟")) return;
    try {
      await api.post(`/records/${id}/submit`);
      toast.success("تم إرسال التقرير للمشرف بنجاح");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const statusBadge = (r) => {
    if (r.submit_status === "received")
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#3E6A56]/10 text-[#3E6A56]" data-testid={`report-status-${r.id}`}><CheckCircle2 className="w-3 h-3" /> تم الاستلام</span>;
    if (r.submit_status === "submitted")
      return <span className="text-xs px-2 py-1 rounded-full bg-[#DDA74A]/15 text-[#a8791f]" data-testid={`report-status-${r.id}`}>بانتظار الاستلام</span>;
    return <span className="text-xs px-2 py-1 rounded-full bg-[#F9F8F6] text-[#4A554E] border border-[#e6e2d8]" data-testid={`report-status-${r.id}`}>مسودة</span>;
  };

  return (
    <div className="space-y-8">
      <RecordApproval section="monthly_report" counselorId={counselorId} counselorGender={ownerGender} contentCount={reports.length} />
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#3E6A56]/10 text-[#3E6A56] flex items-center justify-center">
          <BarChart3 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>
            التقرير الشهري
          </h2>
          <p className="text-sm text-[#4A554E]">إعداد تقرير شهري شامل مع مصفوفة البرامج والصور والحالات</p>
        </div>
      </div>

      {!readOnly && (
        <div ref={printRef} className="bg-white rounded-2xl border border-[#e6e2d8] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-6 sm:p-8 space-y-6">
          <div className="pb-4 border-b border-[#f0ebe0]">
            <SchoolDocumentHeader schoolKey={schoolKey} gender={ownerGender} schoolName={schoolName || user?.school_name} />
            <div className="text-center text-sm font-bold text-[#3E6A56] mt-2" style={{ fontFamily: "IBM Plex Sans Arabic" }}>
              التقرير الشهري للتوجيه الطلابي
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">الشهر</Label>
            <Select value={form.month} onValueChange={(v) => setForm({ ...form, month: v })}>
              <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8]"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <h3 className="text-lg font-bold text-[#1A201C] mb-3">البرامج المنفذة</h3>
            <div className="overflow-x-auto bg-[#F9F8F6]/40 rounded-xl border border-[#e6e2d8]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F9F8F6]">
                    <th className="text-start px-4 py-2 font-semibold">البرنامج</th>
                    <th className="text-center px-4 py-2 font-semibold w-20">نُفذ</th>
                    <th className="text-start px-4 py-2 font-semibold">ملاحظات</th>
                  </tr>
                </thead>
                <tbody>
                  {PROGRAMS.map((p) => (
                    <tr key={p} className="border-t border-[#f0ebe0]">
                      <td className="px-4 py-2">{p}</td>
                      <td className="px-4 py-2 text-center">
                        <Checkbox
                          checked={form.programs[p]?.done || false}
                          onCheckedChange={(v) => setForm({ ...form, programs: { ...form.programs, [p]: { ...form.programs[p], done: !!v } } })}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          value={form.programs[p]?.notes || ""}
                          onChange={(e) => setForm({ ...form, programs: { ...form.programs, [p]: { ...form.programs[p], notes: e.target.value } } })}
                          className="rounded-lg bg-white border-[#e6e2d8] h-9"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold text-[#1A201C] mb-3">الحالات المتابعة</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {CASE_TYPES.map((c) => (
                <div key={c} className="bg-[#F9F8F6]/60 rounded-xl border border-[#e6e2d8] p-3">
                  <div className="text-xs text-[#4A554E] mb-1">{c}</div>
                  <Input
                    type="number"
                    value={form.cases[c] || 0}
                    onChange={(e) => setForm({ ...form, cases: { ...form.cases, [c]: Number(e.target.value) || 0 } })}
                    className="rounded-lg bg-white border-[#e6e2d8] h-9"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold text-[#1A201C] mb-3">صور التقرير ({form.photos.length})</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {form.photos.map((src, i) => (
                <div key={i} className="relative aspect-video rounded-xl overflow-hidden border border-[#e6e2d8] bg-[#F9F8F6] group">
                  <img src={src} alt={`ph-${i}`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                    <button type="button" onClick={() => setPreviewPhoto(src)} className="p-2 rounded-lg bg-white text-[#3E6A56]"><Eye className="w-4 h-4" /></button>
                    <button type="button" onClick={() => removePhoto(i)} className="p-2 rounded-lg bg-white text-[#B84B4B]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              <label className="cursor-pointer aspect-video rounded-xl border-2 border-dashed border-[#e6e2d8] hover:border-[#3E6A56] hover:bg-[#3E6A56]/5 flex flex-col items-center justify-center text-[#4A554E] transition">
                <ImagePlus className="w-6 h-6 mb-1" />
                <span className="text-sm">أضف صوراً</span>
                <input type="file" accept="image/*" onChange={addPhoto} multiple className="hidden" data-testid="report-photo-input" />
              </label>
            </div>
            <p className="mt-2 text-xs text-[#4A554E]">لا يوجد حد عددي للصور؛ الحد الإجمالي لحجم الصور 6 ميجابايت.</p>
          </div>

          <div>
            <Label className="mb-1.5 block">ملاحظات واقتراحات</Label>
            <Textarea value={form.suggestions} onChange={(e) => setForm({ ...form, suggestions: e.target.value })} className="rounded-xl bg-white border-[#e6e2d8] min-h-[100px]" />
          </div>

          <PrintSignatures className="pt-6 text-sm" counselorName={form.counselor_name || counselorName} principalName={form.principal_name || principal.name} counselorGender={ownerGender} principalGender={principal.gender} />

          <div className="flex flex-wrap gap-2 pt-4 border-t border-[#f0ebe0] no-print">
            <Button onClick={save} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid="monthly-save-button">
              <Save className="w-4 h-4 me-2" /> {editing ? "تحديث التقرير" : "حفظ التقرير"}
            </Button>
            <Button onClick={exportPDF} variant="outline" className="rounded-xl border-[#e6e2d8]" data-testid="monthly-pdf-button">
              <FileDown className="w-4 h-4 me-2" /> تصدير PDF
            </Button>
            {editing && (
              <Button onClick={() => { setEditing(null); setForm(emptyReport()); }} variant="outline" className="rounded-xl">تقرير جديد</Button>
            )}
          </div>
        </div>
      )}

      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 no-print">
          <h3 className="text-lg font-bold text-[#1A201C]">التقارير المحفوظة</h3>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8] w-full sm:w-52" data-testid="monthly-year-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الأعوام الدراسية</SelectItem>
              {[...new Set(reports.map((r) => academicYearOf(r.created_at)))].sort().reverse().map((y) => (
                <SelectItem key={y} value={y}>العام الدراسي {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="bg-white rounded-2xl border border-[#e6e2d8] shadow-[0_8px_30px_rgb(0,0,0,0.03)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F8F6]"><tr>
              <th className="text-start px-4 py-3">الشهر</th>
              <th className="text-start px-4 py-3">البرامج المنفذة</th>
              <th className="text-start px-4 py-3">التاريخ</th>
              <th className="text-start px-4 py-3">الحالة</th>
              {!readOnly && <th className="text-start px-4 py-3 no-print">الإجراءات</th>}
            </tr></thead>
            <tbody>
              {(() => { const shown = reports.filter((r) => year === "all" || academicYearOf(r.created_at) === year); return (
              loading ? <tr><td colSpan={readOnly ? 4 : 5} className="text-center py-8">جاري التحميل...</td></tr>
              : shown.length === 0 ? <tr><td colSpan={readOnly ? 4 : 5} className="text-center py-8 text-[#4A554E]">لا توجد تقارير محفوظة</td></tr>
              : shown.map((r) => {
                const done = Object.values(r.data.programs || {}).filter((p) => p?.done).length;
                return (
                  <tr key={r.id} className="border-t border-[#f0ebe0]">
                    <td className="px-4 py-3 font-semibold text-[#3E6A56]">{r.data.month}</td>
                    <td className="px-4 py-3">{done} / {PROGRAMS.length}</td>
                    <td className="px-4 py-3 text-[#4A554E]">{new Date(r.created_at).toLocaleDateString("ar-EG")}</td>
                    <td className="px-4 py-3">
                      {statusBadge(r)}
                      {readOnly && <details className="no-print my-2 text-sm">
                        <summary className="cursor-pointer font-semibold">عرض التقرير للاطلاع</summary>
                        <div className="space-y-2 py-2">
                          <p>{staffTitles(ownerGender).counselor}: {r.data.counselor_name || counselorName} — {staffTitles(principal.gender || ownerGender).principal}: {r.data.principal_name || principal.name}</p>
                          {Object.entries(r.data.programs || {}).filter(([, value]) => value?.done).map(([name, value]) => <p key={name}>{name}: {value.notes || "تم التنفيذ"}</p>)}
                          {Object.entries(r.data.cases || {}).map(([name, value]) => <p key={name}>{name}: {value}</p>)}
                          {r.data.suggestions && <p className="whitespace-pre-wrap">{r.data.suggestions}</p>}
                          {(r.data.photos || []).map((src, index) => <img key={index} src={src} alt={`شاهد التقرير ${index + 1}`} className="max-h-48 max-w-full object-contain" />)}
                        </div>
                      </details>}
                    </td>
                    {!readOnly && (
                      <td className="px-4 py-3 no-print">
                        <div className="flex gap-1 items-center">
                          {r.submit_status !== "received" && r.submit_status !== "submitted" && (
                            <Button size="sm" variant="ghost" onClick={() => submitToSupervisor(r.id)} className="h-8 px-2 text-[#3E6A56] hover:bg-[#3E6A56]/10" title="إرسال للمشرف" data-testid={`submit-report-${r.id}`}>
                              <Send className="w-4 h-4 me-1" /> <span className="text-xs">إرسال للمشرف</span>
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => editReport(r)} className="h-8 w-8 p-0 text-[#3E6A56]">✏️</Button>
                          <Button size="sm" variant="ghost" onClick={() => del(r.id)} className="h-8 w-8 p-0 text-[#B84B4B]"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
              ); })()}
            </tbody>
          </table>
        </div>
      </div>

      {previewPhoto && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setPreviewPhoto(null)}>
          <img src={previewPhoto} alt="preview" className="max-w-full max-h-full rounded-2xl" />
        </div>
      )}
    </div>
  );
}
